/**
 * Easing functions applied to the raw 0→1 edge-overshoot progress before it is
 * written to `--scroll-stack-progress`. Plain cubics — no dependencies, no
 * allocation, safe to call once per in-zone item per frame.
 */

/**
 * @param {number} progress - raw progress, 0→1
 * @returns {number} the same progress
 */
export function linear(progress) {
	return progress;
}

/**
 * @param {number} progress - raw progress, 0→1
 * @returns {number} cubic ease-in
 */
export function easeIn(progress) {
	return progress * progress * progress;
}

/**
 * @param {number} progress - raw progress, 0→1
 * @returns {number} cubic ease-out
 */
export function easeOut(progress) {
	const inverse = 1 - progress;
	return 1 - inverse * inverse * inverse;
}

/**
 * @param {number} progress - raw progress, 0→1
 * @returns {number} cubic ease-in-out
 */
export function easeInOut(progress) {
	if (progress < 0.5) return 4 * progress * progress * progress;
	const inverse = -2 * progress + 2;
	return 1 - (inverse * inverse * inverse) / 2;
}

const EASINGS = {
	linear,
	'ease-in': easeIn,
	'ease-out': easeOut,
	'ease-in-out': easeInOut,
};

const warnedNames = new Set();

/**
 * Resolves an easing name to its function. Unknown names fall back to linear
 * and warn once per name.
 * @param {string} [name] - one of `linear`, `ease-in`, `ease-out`, `ease-in-out`
 * @returns {(progress: number) => number} the easing function
 */
export function resolveEasing(name) {
	if (!name) return linear;
	const key = String(name).trim();
	if (!key) return linear;
	const easing = EASINGS[key];
	if (easing) return easing;
	if (!warnedNames.has(key)) {
		warnedNames.add(key);
		console.warn(
			`<scroll-stack> unknown easing "${key}" — falling back to linear. ` +
				`Known easings: ${Object.keys(EASINGS).join(', ')}.`
		);
	}
	return linear;
}
